//@ts-nocheck
import RefundCancelBookingModal from '../RefundCancelBookingModal/RefundCancelBookingModal';
import Skeleton from '@mui/material/Skeleton';
import ManageBooking from '../ManageBooking/ManageBooking';
import { Box, Modal, Menu, MenuItem, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { XGButton, XGInput } from '@xg-cl/xg-cl';
import { useState, useEffect } from 'react';
import { Link, DotsThreeVertical, Wallet, Info } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { format as formatDate, utcToZonedTime } from 'date-fns-tz';
import { formatCurrency } from '../../../../utils/currencyHelper';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getTrip } from '../../../../services/trip/trip';
import { getTripBookings } from '../../../../services/booking/booking';
import './BookingsPanel.css';

const BookingsPanel = () => {
  const { t } = useTranslation(['bookings']);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [linkCopied, setLinkCopied] = useState('Copy link');
  const [paymentLink, setPaymentLink] = useState(null);
  const [bookingList, setBookingList] = useState([]);
  const { tripId } = useLoaderData();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [tripStartDate, setTripStartDate] = useState(null);
  const [bookingsData, setBookingsData] = useState(null);
  const [bookingDataLoading, setBookingDataLoading] = useState(null);
  const [bookingsDataError, setBookingsDataError] = useState(null);
  const [tenantData, setTenanData] = useState(null);
  const [displayManageBooking, setDisplayManageBooking] = useState(false);
  const { showRefunds, showManageBooking } = useFlags();

  useEffect(() => {
    const tenantDataResult = JSON.parse(localStorage.getItem('tenantData'));
    setTenanData(tenantDataResult);
  }, []);

  const currencycode = tenantData?.sellingcurrency?.currencycode;
  const currencysymbol = tenantData?.sellingcurrency?.currencysymbol;

  const dialogTypeOption = {
    requestPayment: 'REQUEST_PAYMENT',
    manageBooking: 'MANAGE_BOOKING',
    cancelBooking: 'CANCEL_BOOKING',
    refund: 'REFUND'
  };
  const handleMenuOpen = (event, booking) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedBooking(null);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setDialogType(null);
    setPaymentLink(null);
    // refresh the bookings data
    getTripBookings(tripId).then((res) => {
      setBookingsData(res.data);
      setBookingDataLoading(res.isLoading);
      setBookingsDataError(res.error);
    });
    handleMenuClose();
  };

  const handleRefund = () => {
    setDialogType(dialogTypeOption.refund);
    setOpenDialog(true);
  };

  const handleManageBookingOpen = async () => {
    setDisplayManageBooking(true);
  };

  const handleManageBookingClose = async () => {
    setMenuAnchorEl(null);
    setDisplayManageBooking(false);
  };

  const handleCancelBooking = () => {
    setDialogType(dialogTypeOption.cancelBooking);
    setOpenDialog(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(paymentLink);
    setLinkCopied('Link copied');
    setTimeout(() => {
      setLinkCopied('Copy link');
    }, 1500);
  };

  useEffect(() => {
    getTripBookings(tripId).then((res) => {
      setBookingsData(res.data);
      setBookingDataLoading(res.isLoading);
      setBookingsDataError(res.error);
    });
  }, [tripId]);

  useEffect(() => {
    const fetchTripStartDate = async () => {
      try {
        const res = await getTrip(tripId);
        const startDate = res?.data?.startdate
          ? formatDate(
              utcToZonedTime(new Date(res.data.startdate), 'UTC'),
              'dd MMM yyyy',
              { timeZone: 'UTC' }
            )
          : null;
        setTripStartDate(startDate);
      } catch (error) {
        console.error('Error fetching trip start date:', error);
        setTripStartDate(null);
      }
    };

    fetchTripStartDate();
  }, [tripId]);

  const extractAmount = (str) => {
    const match = str.match(/[\d,.]+/);
    return match ? parseFloat(match[0].replace(/,/g, '')) : null;
  };

  useEffect(() => {
    if (bookingsData && bookingsData.length > 0) {
      const newBookingsData = bookingsData
        .map((el, index) => {
          return {
            id: index,
            ...el,
            totalPrice: formatCurrency(el?.totalPrice),
            balance: formatCurrency(el?.balance),
            amountReceived: formatCurrency(el?.amountReceived),
            amountPending: formatCurrency(el?.amountInTransit),
            finalpaymentduedate: el.finalpaymentduedate
              ? formatDate(
                  utcToZonedTime(new Date(el.finalpaymentduedate), 'UTC'),
                  'dd MMM yyyy',
                  { timeZone: 'UTC' }
                )
              : '-',
            unformatBalance: el.balance
          };
        })
        // sort by bookingNumber in descending order
        .sort((a, b) => {
          const dateA = a.bookingNumber;
          const dateB = b.bookingNumber;
          return dateB - dateA;
        });
      setBookingList(newBookingsData);
    }
  }, [bookingsData, bookingDataLoading, bookingsDataError]);

  const bookingsColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90
    },
    {
      field: 'bookingNumber',
      headerName: t('bookings:bookingNumber'),
      headerClassName: 'booking-panel-header',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { row = {} } = params;
        const { bookingNumber, bookingStatus } = row;
        return (
          <div className={'booking-panel-booking-number'}>
            <div
              className={`booking-panel-booking-number-badge ${bookingStatus?.statuscode === 'CANCELLED' ? 'cancelled' : ''}`}
            >
              #{bookingNumber}
            </div>
            {bookingStatus?.statuscode === 'CANCELLED' && (
              <div className='booking-panel-booking-number-cancelled-text'>
                {t('bookings:cancelled').toUpperCase()}
              </div>
            )}
          </div>
        );
      }
    },
    {
      field: 'bookingOwner',
      headerName: t('bookings:primaryPassenger'),
      headerClassName: 'booking-panel-header',
      type: 'string',
      width: 250,
      align: 'left',
      headerAlign: 'center',
      renderCell: (params) => {
        const { row = {} } = params;
        const { bookingOwner } = row;
        return (
          <div className='booking-panel-booking-owner'>
            {bookingOwner?.name}
          </div>
        );
      }
    },
    {
      field: 'participants',
      headerName: t('bookings:participants'),
      headerClassName: 'booking-panel-header',
      type: 'number',
      width: 130,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'totalPrice',
      headerName: t('bookings:totalPrice'),
      headerClassName: 'booking-panel-header',
      type: 'string',
      width: 160,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'amountReceived',
      headerName: t('bookings:amountReceived'),
      headerClassName: 'booking-panel-header',
      type: 'string',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { row = {} } = params;
        const {
          amountReceived,
          amountInTransit,
          bookingStatus,
          refundedAmount
        } = row;
        const amountInTransitFormatted = `${currencysymbol} ${amountInTransit} ${currencycode}`;
        return (
          <div>
            <div className='booking-panel-booking-amountReceived-text'>
              {amountInTransit > 0 ? (
                <Tooltip
                  title={
                    <div>
                      {amountInTransitFormatted}
                      <br />
                      {t('bookings:achPaymentTakes')}
                    </div>
                  }
                >
                  <span className='booking-panel-booking-amountReceived-icon'>
                    <Info size={12} />
                  </span>
                </Tooltip>
              ) : (
                ''
              )}
              {amountReceived}
            </div>
            {bookingStatus?.statuscode === 'CANCELLED' && (
              <div className='booking-panel-booking-amountReceived-refund-text'>
                {t('bookings:refunded').toUpperCase() +
                  ' ' +
                  currencysymbol +
                  refundedAmount +
                  ' ' +
                  currencycode}
              </div>
            )}
          </div>
        );
      }
    },
    {
      field: 'balance',
      headerName: t('bookings:balance'),
      headerClassName: 'booking-panel-header',
      type: 'string',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { row = {} } = params;
        const { balance, bookingStatus } = row;
        return (
          <div
            className={
              bookingStatus?.statuscode == 'CANCELLED'
                ? 'booking-panel-balance-badge'
                : ''
            }
          >
            {bookingStatus?.statuscode == 'CANCELLED' ? 'N/A' : balance}
          </div>
        );
      }
    },
    {
      field: 'finalpaymentduedate',
      headerName: t('bookings:nextPayment'),
      headerClassName: 'booking-panel-header',
      type: 'number',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { row = {} } = params;
        const { finalpaymentduedate, bookingStatus, balance } = row;
        return bookingStatus?.statuscode == 'CANCELLED' ||
          extractAmount(balance) == 0 ? (
          <div className='booking-panel-final-payment-due-date-cancelled-badge'>
            N/A
          </div>
        ) : (
          <div className='booking-panel-final-payment-due-date'>
            <div>{finalpaymentduedate}</div>
            <div
              className={
                balance > 0 && new Date(finalpaymentduedate) < Date.now()
                  ? 'booking-panel-final-payment-due-date-badge-cancelled'
                  : 'booking-panel-final-payment-due-date-badge'
              }
            >
              {balance}
            </div>
          </div>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('bookings:actions'),
      headerClassName: 'booking-panel-header',
      type: 'number',
      width: 195,
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const { row = {} } = params;
        const { bookingKey, bookingNumber, unformatBalance } = row;

        return showRefunds ? (
          <XGButton
            className='booking-panel-columns-button'
            data-testid='booking-actions-menu-button'
            id='booking-actions-menu-button'
            type='secondary'
            size='small'
            variant=''
            startIcon={<DotsThreeVertical size={24} />}
            onClick={(event) => handleMenuOpen(event, row)}
          />
        ) : unformatBalance > 0 ? (
          <XGButton
            className='booking-panel-columns-button'
            data-testid='share-dialog-close-button'
            id='dialog-close-button'
            text={t('bookings:requestPayment')}
            type='secondary'
            size='small'
            variant='outlined'
            startIcon={<Wallet size={12} />}
            onClick={() => {
              setDialogType(dialogTypeOption.requestPayment);
              setPaymentLink(
                `${window.location.origin}/bookings/${bookingNumber}/payment?bookingkey=${bookingKey}`
              );
              setOpenDialog(true);
            }}
          />
        ) : (
          <></>
        );
      }
    }
  ];

  const boxHeight = bookingList.length === 0 ? '100px' : 'auto';

  if (bookingsDataError) {
    return <>Error</>;
  } else if (bookingDataLoading) {
    return (
      <Skeleton
        variant='rectangular'
        width={'100%'}
        height={1000}
        sx={{ border: '1px solid #EAECF0' }}
      />
    );
  } else {
    return (
      <>
        {!displayManageBooking ? (
          <Box className='booking-panel-container' height={boxHeight}>
            <Box className='booking-panel-header-info'>
              <div className='booking-panel-header-info-item'>
                <div className='booking-panel-header-info-item-title'>
                  {t('bookings:tripTotal')}
                </div>
                <div className='booking-panel-header-info-item-value'>
                  {bookingsData?.length > 0
                    ? formatCurrency(
                        bookingsData.reduce((acc, booking) => {
                          if (
                            booking.bookingStatus?.statuscode !== 'CANCELLED'
                          ) {
                            return acc + booking.totalPrice;
                          } else {
                            return acc;
                          }
                        }, 0)
                      )
                    : `${currencysymbol} 0.00 ${currencycode}`}
                </div>
              </div>
              <div className='booking-panel-header-info-separator'></div>
              <div className='booking-panel-header-info-item'>
                <div className='booking-panel-header-info-item-title'>
                  {t('bookings:totalReceived')}
                </div>
                <div className='booking-panel-header-info-item-value'>
                  {bookingsData?.length > 0
                    ? formatCurrency(
                        bookingsData.reduce(
                          (acc, booking) => acc + booking.amountReceived,
                          0
                        )
                      )
                    : `${currencysymbol} 0.00 ${currencycode}`}
                </div>
              </div>
              <div className='booking-panel-header-info-separator'></div>
              <div className='booking-panel-header-info-item'>
                <div className='booking-panel-header-info-item-title'>
                  {t('bookings:totalDue')}
                </div>
                <div className='booking-panel-header-info-item-value'>
                  {bookingsData?.length > 0
                    ? formatCurrency(
                        bookingsData.reduce((acc, booking) => {
                          if (
                            booking.bookingStatus?.statuscode !== 'CANCELLED'
                          ) {
                            return acc + booking.balance;
                          } else {
                            return acc;
                          }
                        }, 0)
                      )
                    : `${currencysymbol} 0.00 ${currencycode}`}
                </div>
              </div>
              <div className='booking-panel-header-info-separator'></div>
              <div className='booking-panel-header-info-item'>
                <div className='booking-panel-header-info-item-title'>
                  {t('bookings:totalPassengers')}
                </div>
                <div className='booking-panel-header-info-item-value'>
                  {bookingsData?.length > 0
                    ? bookingsData.reduce((acc, booking) => {
                        if (booking.bookingStatus?.statuscode !== 'CANCELLED') {
                          return acc + booking.participants;
                        } else {
                          return acc;
                        }
                      }, 0)
                    : '0'}
                </div>
              </div>
            </Box>
            <DataGrid
              className='booking-panel-datagrid'
              columns={bookingsColumns}
              rows={bookingList}
              initialState={{
                columns: { columnVisibilityModel: { id: false } }
              }}
              hideFooter
              disableColumnMenu
              disableColumnFilter
              localeText={{ noRowsLabel: t('common:noDataAvailable') }}
            />
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
            >
              {selectedBooking?.unformatBalance > 0 &&
                selectedBooking?.bookingStatus?.statuscode !== 'CANCELLED' && (
                  <MenuItem
                    onClick={() => {
                      setDialogType(dialogTypeOption.requestPayment);
                      setPaymentLink(
                        `${window.location.origin}/bookings/${selectedBooking.bookingNumber}/payment?bookingkey=${selectedBooking.bookingKey}`
                      );
                      setOpenDialog(true);
                      handleMenuClose();
                    }}
                  >
                    {t('bookings:requestPayment')}
                  </MenuItem>
                )}

              {showManageBooking && (
                <MenuItem onClick={handleManageBookingOpen}>
                  {t('bookings:manageBooking')}
                </MenuItem>
              )}
              <MenuItem onClick={handleRefund}>{t('bookings:refund')}</MenuItem>
              {selectedBooking?.bookingStatus?.statuscode !== 'CANCELLED' && (
                <MenuItem onClick={handleCancelBooking}>
                  {t('bookings:cancelBooking')}
                </MenuItem>
              )}
            </Menu>
            <Modal open={openDialog} onClose={closeDialog}>
              <Box
                className={`booking-panel-modal-box ${dialogType === dialogTypeOption.cancelBooking || dialogType === dialogTypeOption.refund ? 'booking-panel-cancel-modal-box' : ''}`}
              >
                {dialogType === dialogTypeOption.requestPayment && (
                  <>
                    <h3 className='booking-panel-modal-title'>
                      {t('bookings:requestPayment')}
                    </h3>
                    <h3 className='request-dialog__subtitle text-sm-medium'>
                      {'Link to share'}
                    </h3>
                    <XGInput
                      name='link'
                      id='trips-link-input'
                      readOnly={true}
                      type='text'
                      value={paymentLink}
                      icon
                      customIcon={
                        <div
                          className='booking-panel-copy-link'
                          onClick={copyToClipboard}
                        >
                          <Link size={20} />
                          <span style={{ marginLeft: '8px' }}>
                            {linkCopied}
                          </span>
                        </div>
                      }
                    />
                    <div className='booking-panel-dialog-close-button'>
                      <XGButton
                        id='dialog-close-button'
                        text={t('common:close')}
                        type='secondary'
                        size='large'
                        variant='outlined'
                        onClick={closeDialog}
                      ></XGButton>
                    </div>
                  </>
                )}
                {dialogType === dialogTypeOption.refund && (
                  <RefundCancelBookingModal
                    booking={selectedBooking}
                    tripStartDate={tripStartDate}
                    onCancel={closeDialog}
                    type={dialogTypeOption.refund}
                  />
                )}
                {dialogType === dialogTypeOption.cancelBooking && (
                  <RefundCancelBookingModal
                    booking={selectedBooking}
                    tripStartDate={tripStartDate}
                    onCancel={closeDialog}
                    type={dialogTypeOption.cancelBooking}
                  />
                )}
              </Box>
            </Modal>
          </Box>
        ) : (
          <>
            {showManageBooking && (
              <ManageBooking
                bookingId={selectedBooking?.bookingNumber}
                handleManageBookingClose={handleManageBookingClose}
              />
            )}
          </>
        )}
      </>
    );
  }
};

export default BookingsPanel;
